import { useCallback, useEffect, useState } from "react";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaUserCheck, FaUserGroup } from "react-icons/fa6";
import { IoSearchOutline } from "react-icons/io5";
import { FollowersTab } from "./tabs/FollowersTab";
import { FollowingTab } from "./tabs/FollowingTab";
import {
    UserAccountFollowerlist,
    UserAccountFollowinglist,
    searchMyFollowersList,
    searchMyFollowingList,
} from "../../redux/ApiSlice/AccountProfileSlice";

const AccountConnections = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { type } = useParams();
    const [activeTab, setActiveTab] = useState(type || 'followers');
    const [searchTerm, setSearchTerm] = useState("");
    const [followersPage, setFollowersPage] = useState(1);
    const [followingPage, setFollowingPage] = useState(1);

    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const {
        User_followingList,
        User_followersList,
        User_profile,
        myFollowersHasMore,
        myFollowingHasMore,
        loadingMoreMyConnections,
        mySearchedFollowersList,
        mySearchedFollowingList,
        searchingMyFollowers,
        searchingMyFollowing,
    } = ProfileDetails;

    const [debouncePending, setDebouncePending] = useState(false);
    const trimmedSearchTerm = searchTerm.trim();
    const isSearching = !!trimmedSearchTerm;

    // Determine the title based on the type parameter
    const getTitle = () => {
        if (type === 'followers') return 'Followers';
        if (type === 'following') return 'Following';
        return 'Connections';
    };

    useEffect(() => {
        if (type) {
            setActiveTab(type);
        }
    }, [type]);

    useEffect(() => {
        if (activeTab === 'followers') {
            setFollowersPage(1);
            dispatch(UserAccountFollowerlist({ page: 1 }));
        } else if (activeTab === 'following') {
            setFollowingPage(1);
            dispatch(UserAccountFollowinglist({ page: 1 }));
        }
    }, [activeTab, dispatch]);

    // Debounced search against the own-list search API
    useEffect(() => {
        if (!trimmedSearchTerm) {
            setDebouncePending(false);
            return;
        }
        setDebouncePending(true);
        const handle = setTimeout(() => {
            setDebouncePending(false);
            if (activeTab === 'followers') {
                dispatch(searchMyFollowersList({ q: trimmedSearchTerm }));
            } else if (activeTab === 'following') {
                dispatch(searchMyFollowingList({ q: trimmedSearchTerm }));
            }
        }, 400);
        return () => clearTimeout(handle);
    }, [trimmedSearchTerm, activeTab, dispatch]);

    const handleLoadMoreFollowers = useCallback(() => {
        const nextPage = followersPage + 1;
        setFollowersPage(nextPage);
        dispatch(UserAccountFollowerlist({ page: nextPage, isLoadMore: true }));
    }, [followersPage, dispatch]);

    const handleLoadMoreFollowing = useCallback(() => {
        const nextPage = followingPage + 1;
        setFollowingPage(nextPage);
        dispatch(UserAccountFollowinglist({ page: nextPage, isLoadMore: true }));
    }, [followingPage, dispatch]);

    const handleTabChange = (tabValue) => {
        setActiveTab(tabValue);
        setSearchTerm(""); // Reset search when switching tabs
        navigate(`/accounts/${tabValue}`);
    };

    return (
        <>
            <div className="sticky top-0 z-50 bg-white dark:bg-[#1E1D21]">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => navigate("/account")}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                    />
                    <p className="font-bold text-[16px] text-black dark:text-black">
                        {getTitle()}
                    </p>
                </div>

                {/* Tabs Navigation */}
                <div className="flex items-center justify-center gap-x-2 mx-2 bg-white dark:bg-[#1E1D21]">
                    <div
                        onClick={() => handleTabChange('followers')}
                        className={`flex justify-center items-center w-full py-2 cursor-pointer border-b-2 ${activeTab === 'followers'
                            ? 'border-light-blue-400 dark:border-white'
                            : 'border-transparent'
                            }`}
                    >
                        <FaUserGroup className={`text-[15px] ${activeTab === 'followers'
                            ? 'text-light-blue-400 dark:text-white'
                            : 'text-black dark:text-white'
                            }`} />
                        <p className={`text-[12px] ml-2 font-semibold ${activeTab === 'followers'
                            ? 'text-light-blue-400 dark:text-white'
                            : 'text-black dark:text-white'
                            }`}>
                            Followers
                        </p>
                    </div>
                    <div
                        onClick={() => handleTabChange('following')}
                        className={`flex justify-center items-center w-full py-2 cursor-pointer border-b-2 ${activeTab === 'following'
                            ? 'border-light-blue-400 dark:border-white'
                            : 'border-transparent'
                            }`}
                    >
                        <FaUserCheck className={`text-[15px] ${activeTab === 'following'
                            ? 'text-light-blue-400 dark:text-white'
                            : 'text-black dark:text-white'
                            }`} />
                        <p className={`text-[12px] ml-2 font-semibold ${activeTab === 'following'
                            ? 'text-light-blue-400 dark:text-white'
                            : 'text-black dark:text-white'
                            }`}>
                            Following
                        </p>
                    </div>
                </div>

                {/* Search Input - Sticky below tabs */}
                <div className="relative mx-2 mt-2 mb-2">
                    <IoSearchOutline className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[18px] text-black dark:text-white" />
                    <input
                        type="text"
                        value={searchTerm}
                        placeholder={activeTab === 'followers' ? "Search" : "Search"}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full text-[14px] pl-8 pr-2 py-1.5 rounded-lg bg-gray-100 dark:bg-[#333333] dark:text-white dark:placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#BDE6FF]"
                    />
                </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white dark:bg-[#1E1D21] h-full w-full">
                {activeTab === 'followers' && (
                    <FollowersTab
                        datalist={isSearching ? mySearchedFollowersList : User_followersList}
                        otheruser_id={false}
                        searchTerm={searchTerm}
                        hasMore={isSearching ? false : myFollowersHasMore}
                        loadingMore={loadingMoreMyConnections}
                        onLoadMore={handleLoadMoreFollowers}
                        serverFiltered={isSearching}
                        searching={isSearching && (debouncePending || searchingMyFollowers)}
                    />
                )}
                {activeTab === 'following' && (
                    <FollowingTab
                        datalist={isSearching ? mySearchedFollowingList : User_followingList}
                        otheruser_id={false}
                        searchTerm={searchTerm}
                        hasMore={isSearching ? false : myFollowingHasMore}
                        loadingMore={loadingMoreMyConnections}
                        onLoadMore={handleLoadMoreFollowing}
                        serverFiltered={isSearching}
                        searching={isSearching && (debouncePending || searchingMyFollowing)}
                    />
                )}
            </div>
        </>
    )
}

export default AccountConnections